/**
 *  [2022] Zettant Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Zettant Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Zettant Incorporated and its
 * suppliers and may be covered by Japan. and Foreign Patents,
 * patents in process, and are protected by trade secret or
 * copyright law. Dissemination of this information or
 * reproduction of this material is strictly forbidden unless
 * prior written permission is obtained from Zettant Incorporated.
 */


/**
 * :ZNOTE:
 * tag: cacheManage
 * note:
 *  - cacheManageの初期化処理
 * sample:
 *  - ほぼ全てのプロジェクト
 * :ZNOTE:
 */

import {Dexie} from 'dexie';
import {BaseDbStructure} from './interfaces';
import {CacheManage} from './cacheManageClass';
import {DummyDexie} from './dummyDexie';
import {newLocalStorageAdapter} from './cacheManageLocalServer';

const DB_VERSION = 7;
let disabled = false;
const disableIndexedDb = () => disabled = true;  // disableにするとIndexedDbへの読み書きがダミー処理におき変わる

/**
 * IndexedDBのスキーマを定義する (素のcacheManage用)
 * @param tableName {string}
 * @param initParams {{primaryKeyName: string, secondaryKeyName: string}}
 * @param version {number}
 */
const initDbAndTables = (tableName: string, initParams: {
  primaryKeyName: string,
  secondaryKeyName: string
}, version = DB_VERSION): BaseDbStructure => {
  let db: any;
  if (disabled) {
    db = new DummyDexie(['entries', 'meta', 'media']);
  } else {
    db = new Dexie(tableName);
    db.version(version).stores({
      entries: `[${initParams.primaryKeyName}+${initParams.secondaryKeyName}]`,
      meta: 'name',
      media: 'filePath,lastAccessedAt',
    });
  }
  return {
    db,
    entries: db.entries,
    meta: db.meta,
    media: db.media,
  };
};

const initLocalStorageTables = (tableName: string, initParams: {
  primaryKeyName: string,
  secondaryKeyName: string
}, version = DB_VERSION): BaseDbStructure => (
  {
    db: {
      open: () => {},
      delete: () => {},
    } as any,
    entries: newLocalStorageAdapter(tableName, 'entries', initParams.primaryKeyName, initParams.secondaryKeyName) as any,
    meta: newLocalStorageAdapter(tableName, 'meta', 'name') as any,
    media: newLocalStorageAdapter(tableName, 'media', 'filePath') as any,
  } as BaseDbStructure
);

const init = async (tableName: string, primaryKeyName: string, secondaryKeyName: any, email?: string, localStorageMode = false): Promise<CacheManage> => {
  let cache: CacheManage;
  if (!localStorageMode) {
    cache = new CacheManage(tableName, primaryKeyName, secondaryKeyName, initDbAndTables, {
      primaryKeyName,
      secondaryKeyName
    });
  } else {
    cache = new CacheManage(tableName, primaryKeyName, secondaryKeyName, initLocalStorageTables, {
      primaryKeyName,
      secondaryKeyName
    });
  }

  const db = cache.getDb();
  try {
    await db.db.open();
  } catch (d) {
    await db.db.delete(); // 何かおかしいので、一度キャッシュを削除して作り直す
    if (!localStorageMode) {
      cache = new CacheManage(tableName, primaryKeyName, secondaryKeyName, initDbAndTables, {
        primaryKeyName,
        secondaryKeyName
      });
    } else {
      cache = new CacheManage(tableName, primaryKeyName, secondaryKeyName, initLocalStorageTables, {
        primaryKeyName,
        secondaryKeyName
      });
    }
  }
  await classInit(cache, tableName, primaryKeyName, secondaryKeyName, email);
  return cache;
};

/**
 * あらゆるクラスで利用する共通の初期化処理（メタ情報を書き込む）
 * @param cache {<T extends CacheManage>}
 * @param tableName {string}
 * @param primaryKeyName {string}
 * @param secondaryKeyName {string}
 * @param email {string}
 */
const classInit = async <T extends CacheManage>(cache: T, tableName: string, primaryKeyName: string, secondaryKeyName: any, email?: string) => {
  const db = cache.getDb();
  const latest = await db.meta.get('latest');
  if (typeof latest === 'undefined') {
    await db.meta.put({name: 'latest', value: {}});
  } else {
    cache.setLatestTimestampInfo(latest.value);
  }

  await db.meta.put({name: 'tableName', tableName});
  await db.meta.put({name: 'primaryKey', primaryKeyName});
  await db.meta.put({name: 'secondaryKey', secondaryKeyName});
};

export {init, classInit, disableIndexedDb, DB_VERSION};
